import { useCallback } from 'react';
import { useDispatch, useSelector } from 'store';
import { closeTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { cn } from 'utils/cn';

// The panel on the window's end side (the right, left to right) that `DrawerPortal type="temporary"` renders
// into (notifications, tasks, the inbox). It
// is not modal: the panel manages its own focus, and the page stays usable beside it. The destination
// stays mounted while the panel is closed, since the portal looks it up when it renders. The panel slides in
// as it opens and slides back out as it closes, after which it hides. `detached` draws
// it as the new design's box instead of flush under the classic header: `width` px wide, edged by its shadow's
// ring, with rounded corners, at the top and the gap off the window's end edge and bottom that the
// new layout keeps on the document as `--panel-top` and `--panel-gap`. That box slides in 70% of its width
// out of a fade and a blur, and as it closes fades out quicker into the blur while it moves 70% back, after
// which it hides; under reduced motion it fades both ways in 150ms and does not move. Each slide has an `rtl:`
// twin the other way.
function TemporaryDrawer({ detached }: { detached?: { width: number } }) {
    const isOpen = useSelector((state) => !!state.temporaryDrawer.activeDrawerId.length);
    const noBackdrop = useSelector((state) => state.temporaryDrawer.noBackdrop);
    const dispatch = useDispatch();

    const closeDrawer = useCallback(() => dispatch(closeTemporaryDrawer()), [dispatch]);

    return (
        <>
            <div className={detached ? 'relative' : 'relative top-[41px] h-[calc(100%-41px)]'}>
                <div
                    className={cn(
                        'bg-paper border-divider absolute inset-y-0 end-0 z-(--z-drawer) flex flex-col overflow-hidden border-s transition-[translate,display] transition-discrete duration-225 ease-out motion-reduce:transition-none starting:translate-x-full rtl:starting:-translate-x-full',
                        !isOpen && !detached && 'hidden translate-x-full duration-200 rtl:-translate-x-full',
                        // `display` changes last, so the slide or the fade plays first.
                        detached &&
                            'shadow-panel end-(--panel-gap) top-(--panel-top) bottom-(--panel-gap) rounded-2xl border-0 outline-hidden transition-[translate,opacity,filter,display] transition-discrete duration-(--duration-slow) ease-(--ease-smooth-out) motion-reduce:transition-[opacity,display] motion-reduce:duration-150 starting:translate-x-[70%] starting:opacity-0 motion-safe:starting:blur-(--blur-panel) rtl:starting:-translate-x-[70%]',
                        detached &&
                            !isOpen &&
                            'hidden opacity-0 duration-(--duration-fast) motion-safe:translate-x-[70%] motion-safe:blur-(--blur-panel) motion-safe:rtl:-translate-x-[70%]'
                    )}
                    style={detached && { width: detached.width }}
                >
                    <div id="temporary-drawer-portal-destination" className="overflow-hidden" />
                </div>
            </div>
            {/* A click on the page closes the panel. Transparent, and under the floating buttons (which
                stack at the launcher's tier and just below it) and the app chrome. */}
            {isOpen && !noBackdrop && (
                <div
                    aria-hidden
                    className="fixed inset-0 z-[calc(var(--z-floating-ai-launcher)-3)]"
                    onClick={closeDrawer}
                />
            )}
        </>
    );
}

export default TemporaryDrawer;
